import { Injectable } from '@angular/core';
import { TranslocoMissingHandler, TranslocoMissingHandlerData } from '@jsverse/transloco';

/**
 * Surfaces missing translation keys in every environment (including
 * production), where Transloco's default handler is silent.
 *
 * The default `DefaultMissingHandler` only logs when
 * `config.prodMode === false`, so a stale i18n dictionary in prod —
 * e.g. a returning user whose browser cached `en.json` from before a
 * key was added — renders the raw key without any diagnostic. That is
 * precisely how the "raw keys appear at random" incident stayed
 * invisible for so long; the deploy cache-control fix in
 * `.github/actions/deploy-fe/action.yaml` closes the root cause, and
 * this handler ensures the next regression of the same shape is
 * observable in browser dev tools and support session recordings
 * instead of silently degrading the UI.
 *
 * Return value stays `key` to match Transloco's default fallback —
 * users still see the raw key, but at least it is now flagged.
 *
 * Dedupe: `Set<lang::key>` prevents a component that renders a missing
 * key on every change-detection cycle from flooding the console. The
 * set is per-service-instance; Transloco creates the handler once as a
 * root singleton, so the map lives for the tab's lifetime. Bounded by
 * dictionary size (thousands of strings at most, trivial memory).
 */
@Injectable({ providedIn: 'root' })
export class ReportingMissingHandler implements TranslocoMissingHandler {
  private readonly seen = new Set<string>();

  handle(key: string, data: TranslocoMissingHandlerData): string {
    const lang = data.activeLang;
    const dedupeKey = `${lang}::${key}`;
    if (!this.seen.has(dedupeKey)) {
      this.seen.add(dedupeKey);
      console.warn(`[Transloco] Missing translation: "${key}" (lang=${lang})`);
    }
    return key;
  }
}
